type ClarificationOptionLike = {
  title?: unknown;
};

type ClarificationPayloadLike = {
  question?: unknown;
  clarification_title?: unknown;
  clarification_subtitle?: unknown;
  other_option_label?: unknown;
  clarification_copy_variant_id?: unknown;
  options?: unknown;
};

const METADATA_LINE_PATTERNS = [
  /^Media:\s*.+$/i,
  /^Media:\s*(?:\/)?uploads\/.+$/i,
  /^\[Extracted text from uploaded (?:image|document)\]$/i,
];

const hasOptions = (clarification?: ClarificationPayloadLike | null): clarification is ClarificationPayloadLike => {
  return Boolean(
    clarification &&
    Array.isArray(clarification.options) &&
    clarification.options.length > 0,
  );
};

const stripTutorMetadata = (content: string): string => {
  const cleaned = String(content || '')
    .split('\n')
    .map((line) => line.replace(/\r$/, ''))
    .filter((line) => {
      const trimmed = line.trim();
      return !METADATA_LINE_PATTERNS.some((pattern) => pattern.test(trimmed));
    })
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  return cleaned;
};

export const sanitizeClarificationMessageContent = (
  content: string,
  clarification?: ClarificationPayloadLike | null,
): string => {
  const rawContent = String(content || '');
  const sanitizedBase = stripTutorMetadata(rawContent);
  if (!hasOptions(clarification)) {
    return sanitizedBase;
  }

  const title = String(clarification.clarification_title || '').trim();
  const subtitle = String(clarification.clarification_subtitle || '').trim();
  const question = String(clarification.question || '').trim();
  const wrapperLines = new Set(
    [title, subtitle, question, `${title} ${subtitle}`.trim()]
      .map((value) => value.toLowerCase().trim())
      .filter(Boolean),
  );

  const optionTitles = new Set(
    (clarification.options as unknown[])
      .map((option) => (option && typeof option === 'object' ? option as ClarificationOptionLike : undefined))
      .map((option) => String(option?.title || '').trim().toLowerCase())
      .filter(Boolean),
  );

  const filtered = sanitizedBase
    .split('\n')
    .map((line) => line.trimEnd())
    .filter((line) => {
      const trimmed = line.trim();
      if (!trimmed) return false;
      if (/^\d+[).:-]/.test(trimmed)) return false;
      if (wrapperLines.has(trimmed.toLowerCase())) return false;
      return !optionTitles.has(trimmed.toLowerCase());
    })
    .join('\n')
    .trim();

  if (filtered) {
    return filtered;
  }
  if (!wrapperLines.size && !optionTitles.size) {
    return sanitizedBase;
  }
  return '';
};
